import React, { useCallback } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import { UserLoggedInNavigatorScreenProps } from "../../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { WAWidgetSizeToOverviewSize } from "../../WidgetAdd/utils/WAWidgetSizeToOverviewSize";
import { WidgetSizeT } from "../../localTypes/WidgetSizeT";
import { WASizePreview } from "../../WidgetAdd/WASizePreview/WASizePreview";

import { overviewWidgetSizes } from "../../WidgetAdd/utils/widgetSizes/overviewWidgetSizes";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { View } from "react-native";
import { WAFakeHeader } from "../../WidgetAdd/WAFakeHeader";
import { useSizeClass } from "../../../../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import { WAIntegrationTableQuery } from "./__generated__/WAIntegrationTableQuery.graphql";
import { WAIntegrationTableMutation } from "./__generated__/WAIntegrationTableMutation.graphql";
import { WidgetTable } from "widgets/src/WidgetTable";

type Props = UserLoggedInNavigatorScreenProps<"WidgetAddIntegrationTable">;

export const WAIntegrationTable = withWebModal(WAIntegrationTableC, true);
function WAIntegrationTableC(props: Props) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => <InnerWAIntegrationTable {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerWAIntegrationTable(props: Props & { fetchKey: string }) {
  const data = useLazyLoadQuery<WAIntegrationTableQuery>(
    graphql`
      query WAIntegrationTableQuery(
        $storecastId: ID!
        $input: PreviewWidgetTableInput!
      ) {
        dashboard(id: $storecastId) @required(action: THROW) {
          previewWidgetTable(input: $input) {
            dataSource
            ...WidgetTable_table
          }
        }
      }
    `,
    {
      storecastId: props.route.params.storecastId,
      input: {
        size: "TWO_X_FOUR",
        dataSource: props.route.params.dataSource,
      },
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const [createWidget, loading] = useMutation<WAIntegrationTableMutation>(
    graphql`
      mutation WAIntegrationTableMutation(
        $storecastId: ID!
        $input: CreateWidgetTableInput!
      ) {
        dashboard(dashboardId: $storecastId) {
          createWidgetTable(widget: $input) {
            id
          }
        }
      }
    `
  );

  const onCreate = useCallback(
    (size: WidgetSizeT) => {
      createWidget({
        variables: {
          storecastId: props.route.params.storecastId,
          input: {
            positionX:
              props.route.params.position.x + props.route.params.position.c * 2,
            positionY: props.route.params.position.y,
            size: WAWidgetSizeToOverviewSize(size),
            dataSource: props.route.params.dataSource,
          },
        },
        onCompleted: () => props.navigation.pop(2),
      });
    },
    [
      createWidget,
      props.navigation,
      props.route.params.position.c,
      props.route.params.position.x,
      props.route.params.position.y,
      props.route.params.storecastId,
      props.route.params.dataSource,
    ]
  );

  const isCompact = useSizeClass().width === SizeClass.COMPACT;

  return (
    <View style={{ flex: 1 }}>
      {isCompact ? null : <WAFakeHeader />}
      <WASizePreview
        fullScreen
        loading={loading}
        maxSize={props.route.params.maxSize}
        onCreate={onCreate}
        unitSize={props.route.params.unitSize}
        widgetSizes={overviewWidgetSizes}
        defaultSize={{ w: 2, h: 2 }}
      >
        {data.dashboard.previewWidgetTable ? (
          <WidgetTable
            widget={data.dashboard.previewWidgetTable}
            storecastId={props.route.params.storecastId}
            interactive={false}
            mode="IDLE"
          />
        ) : null}
      </WASizePreview>
    </View>
  );
}
